import { css } from '@emotion/react'
import { useWidth } from 'hooks/useMeasure'
import { partition } from 'util/array'

import { VideochamadaToastModel, VideochamadaToastPosition } from './model-videochamadaToast'
import { useVideochamadaToast } from './useVideochamadaToast'
import { VideochamadaToastItem } from './VideochamadaToastItem'

// Largura de mínima necessária para caber as duas colunas lado a lado:
// 18rem (tamanho do toast) + 1rem (gap entre colunas) + 18rem (tamanho do toast) = 37rem.
const TWO_COLUMN_MIN_WIDTH_PX = 37 * 16

function renderSeparatedColumns(toasts: VideochamadaToastModel[], dismiss: (id: string) => void) {
  const [bottomLeft, bottomRight] = partition(toasts, (toast) => toast.position === 'bottom-left')

  const columns = [
    {
      id: 'bottom-left',
      toasts: bottomLeft,
      css: [styles.column],
    },
    {
      id: 'bottom-right',
      toasts: bottomRight,
      css: [styles.column, styles.columnRight],
    },
  ]

  return (
    <div css={styles.columns}>
      {columns.map(({ id, toasts, css }) => (
        <div key={id} css={css}>
          {toasts.map((toast) => (
            <VideochamadaToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
          ))}
        </div>
      ))}
    </div>
  )
}

function renderStack(toasts: VideochamadaToastModel[], dismiss: (id: string) => void) {
  return (
    <div css={styles.stack}>
      {toasts.map((toast) => (
        <div key={toast.id} css={styles.alignSelf(toast.position)}>
          <VideochamadaToastItem toast={toast} onDismiss={dismiss} />
        </div>
      ))}
    </div>
  )
}

export function VideochamadaToast() {
  const { toasts, dismiss } = useVideochamadaToast()
  const [containerRef, width] = useWidth()

  const canRenderSeparatedColumns = width >= TWO_COLUMN_MIN_WIDTH_PX

  return (
    <div ref={containerRef} css={styles.container}>
      {canRenderSeparatedColumns ? renderSeparatedColumns(toasts, dismiss) : renderStack(toasts, dismiss)}
    </div>
  )
}

const styles = {
  container: css`
    position: absolute;
    inset: 0;
    z-index: 4;
    pointer-events: none;
    display: flex;
    align-items: flex-end;
    padding: 1rem;
  `,
  columns: css`
    flex: 1;
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    align-items: flex-end;
  `,
  column: css`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-start;
    gap: 0.5rem;
  `,
  columnRight: css`
    align-items: flex-end;
  `,
  stack: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: 0.5rem;
  `,
  alignSelf: (position: VideochamadaToastPosition) =>
    css`
      align-self: ${position === 'bottom-left' ? 'flex-start' : 'flex-end'};
      max-width: 100%;
    `,
}
